/**
 * "Open full analytics" — the YOM account-claim popup (architecture §4).
 *
 * Flow: backend mints a single-use claim link → we open it in a 520×720 popup
 * → the owner signs in on YOM → YOM posts `{ type: 'yom:claimed' }` to the
 * opener and closes. If the browser blocks the popup we fall back to a new tab.
 *
 * Security: a postMessage is accepted ONLY when its origin is the claim link's
 * own origin AND that origin is on the YOM allow-list, and (when we hold the
 * popup handle) it came from that window. Any page can postMessage us; without
 * these checks a hostile tab could fake a "claimed" state.
 */
import type { TeamRole, User } from '@/types';

export const CLAIM_POPUP_WIDTH = 520;
export const CLAIM_POPUP_HEIGHT = 720;
export const CLAIMED_MESSAGE_TYPE = 'yom:claimed';

/** YOM dashboard origins we trust. Override with VITE_YOM_APP_ORIGINS (comma list). */
export function yomAppOrigins(): string[] {
  const raw = (import.meta.env.VITE_YOM_APP_ORIGINS as string | undefined) ?? '';
  const list = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return list.length ? list : ['https://yom.aapastech.com'];
}

/** Origin of a URL, or null if it is not an absolute http(s) URL. */
export function originOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.origin : null;
  } catch {
    return null;
  }
}

/**
 * The origin a claim message must come from: the claim URL's origin, and only
 * when it is a trusted YOM origin. Null means "trust nothing".
 */
export function expectedClaimOrigin(
  claimUrl: string,
  allowed: readonly string[] = yomAppOrigins()
): string | null {
  const origin = originOf(claimUrl);
  return origin && allowed.includes(origin) ? origin : null;
}

interface MessageLike {
  origin: string;
  data: unknown;
  source?: unknown;
}

export function isTrustedClaimMessage(
  event: MessageLike,
  expectedOrigin: string | null,
  expectedSource?: unknown
): boolean {
  if (!expectedOrigin || event.origin !== expectedOrigin) return false;
  if (expectedSource && event.source !== expectedSource) return false;
  const data = event.data as { type?: unknown } | null;
  return Boolean(data && typeof data === 'object' && data.type === CLAIMED_MESSAGE_TYPE);
}

const CLAIM_ROLES: readonly TeamRole[] = ['owner', 'admin'];

/** Only team owners and admins may claim the team's YOM account. */
export function canOpenFullAnalytics(user: Pick<User, 'teamRole'> | null | undefined): boolean {
  return Boolean(user?.teamRole && CLAIM_ROLES.includes(user.teamRole));
}

/**
 * Show "Open full analytics"? The backend's `canClaim` wins when it is sent
 * (it knows team membership; staff must never claim a customer's org).
 * Without it, fall back to the team role.
 */
export function showFullAnalytics(
  user: Pick<User, 'teamRole'> | null | undefined,
  canClaim: boolean | null | undefined
): boolean {
  if (typeof canClaim === 'boolean') return canClaim;
  return canOpenFullAnalytics(user);
}

/**
 * Where "Open full analytics" goes once the org is claimed. An explicit
 * backend URL is used only if it is on a trusted YOM origin.
 * TODO(YOM): honour ?project= in the dashboard so this lands on the right site.
 */
export function yomDashboardLink(
  status: { dashboardUrl: string | null; yomProjectId: string | null },
  allowed: readonly string[] = yomAppOrigins()
): string {
  const explicit = status.dashboardUrl;
  const origin = originOf(explicit);
  if (explicit && origin && allowed.includes(origin)) return explicit;
  const base = `${allowed[0]}/app/overview`;
  return status.yomProjectId ? `${base}?project=${encodeURIComponent(status.yomProjectId)}` : base;
}

export type ClaimWindowResult = { mode: 'popup' | 'tab'; win: Window } | { mode: 'blocked' };

/** Centered popup; falls back to a tab; reports 'blocked' if both fail. */
export function openClaimWindow(url: string, w: Window = window): ClaimWindowResult {
  const left = Math.max(0, Math.round(w.screenX + (w.outerWidth - CLAIM_POPUP_WIDTH) / 2));
  const top = Math.max(0, Math.round(w.screenY + (w.outerHeight - CLAIM_POPUP_HEIGHT) / 2));
  // No `noopener`: YOM must be able to postMessage back to window.opener.
  const features = `popup=yes,width=${CLAIM_POPUP_WIDTH},height=${CLAIM_POPUP_HEIGHT},left=${left},top=${top}`;
  const popup = w.open(url, 'yom-claim', features);
  if (popup) return { mode: 'popup', win: popup };
  const tab = w.open(url, '_blank');
  if (tab) return { mode: 'tab', win: tab };
  return { mode: 'blocked' };
}

/**
 * Resolve when a trusted `yom:claimed` message arrives. Returns a cancel fn.
 */
export function listenForClaim(
  expectedOrigin: string | null,
  onClaimed: () => void,
  opts: { source?: unknown; target?: Pick<Window, 'addEventListener' | 'removeEventListener'> } = {}
): () => void {
  const target = opts.target ?? window;
  const handler = (event: MessageEvent) => {
    if (!isTrustedClaimMessage(event, expectedOrigin, opts.source)) return;
    target.removeEventListener('message', handler as EventListener);
    onClaimed();
  };
  target.addEventListener('message', handler as EventListener);
  return () => target.removeEventListener('message', handler as EventListener);
}
